import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Download, AlertTriangle, Save, Trash2, FileStack, Upload } from 'lucide-react';
import { generatePDFReport, generateCombinedReport } from '@/lib/pdf-generator';
import { getSteelGroutBond } from '@/lib/calculations';

export const TechnicalReportTab = ({ 
  pileInputs, 
  soilLayers, 
  results, 
  sensitivityResults, 
  chartRef,
  sensitivityChartRef,
  designLoads, 
  fos,
  scenarios,
  setScenarios,
  activePileName,
  metadata,
  setMetadata,
  narrative,
  setNarrative,
  appendices,
  setAppendices
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingCombined, setIsGeneratingCombined] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');

  // Update pileId in metadata when activePileName changes
  React.useEffect(() => {
    if (activePileName && metadata.pileId !== activePileName) {
      setMetadata(prev => ({ ...prev, pileId: activePileName }));
    }
  }, [activePileName, metadata.pileId, setMetadata]);

  // Handle PDF file upload for appendices
  const handleAppendixUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    const newAppendix = {
      id: Date.now(),
      name: file.name,
      type: type, // 'borehole' or 'other'
      title: type === 'borehole' ? 'Borehole Log' : file.name.replace('.pdf', ''),
      data: arrayBuffer,
      size: file.size
    };

    setAppendices(prev => [...prev, newAppendix]);
    
    // Reset input
    e.target.value = '';
  };

  // Remove an appendix
  const removeAppendix = (id) => {
    setAppendices(prev => prev.filter(a => a.id !== id));
  };

  // Update appendix title
  const updateAppendixTitle = (id, title) => {
    setAppendices(prev => prev.map(a => 
      a.id === id ? { ...a, title } : a
    ));
  };

  // Update metadata field
  const updateMetadata = (key, value) => {
    setMetadata(prev => ({ ...prev, [key]: value }));
  };

  // Update narrative field
  const updateNarrative = (key, value) => {
    setNarrative(prev => ({ ...prev, [key]: value }));
  };

  // Save current state as scenario
  const handleSaveScenario = () => {
    if (!results) {
      alert('Please run analysis first before saving a scenario');
      return;
    }

    const scenarioName = newScenarioName.trim() || `Scenario ${(scenarios?.length || 0) + 1}`;
    
    const newScenario = {
      id: Date.now(),
      name: scenarioName,
      pileId: metadata.pileId || activePileName || 'N/A',
      savedAt: new Date().toISOString(),
      includeInReport: true,
      pileInputs: JSON.parse(JSON.stringify(pileInputs)),
      soilLayers: JSON.parse(JSON.stringify(soilLayers)),
      designLoads: JSON.parse(JSON.stringify(designLoads || {})),
      results: JSON.parse(JSON.stringify(results)),
      fos: fos ? JSON.parse(JSON.stringify(fos)) : null,
      narrative: JSON.parse(JSON.stringify(narrative)),
      metadata: JSON.parse(JSON.stringify(metadata))
    };

    setScenarios(prev => [...(prev || []), newScenario]);
    setNewScenarioName('');
  };

  // Delete a scenario
  const handleDeleteScenario = (id) => {
    setScenarios(prev => prev.filter(s => s.id !== id));
  };

  // Toggle scenario inclusion in report
  const toggleScenarioInclusion = (id) => {
    setScenarios(prev => prev.map(s => 
      s.id === id ? { ...s, includeInReport: !s.includeInReport } : s
    ));
  };

  // Generate single pile PDF report
  const handleGenerateReport = async () => {
    if (!results) {
      alert('Please run analysis first before generating report');
      return;
    }

    setIsGenerating(true);
    
    try {
      await generatePDFReport({
        metadata,
        narrative,
        pileInputs,
        soilLayers,
        results,
        sensitivityResults,
        chartRef,
        sensitivityChartRef,
        designLoads,
        fos,
        appendices
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate combined multi-scenario report
  const handleGenerateCombinedReport = async () => {
    const includedScenarios = scenarios?.filter(s => s.includeInReport) || [];
    
    if (includedScenarios.length === 0) {
      alert('Please select at least one scenario to include in the combined report');
      return;
    }

    setIsGeneratingCombined(true);
    
    try {
      await generateCombinedReport({
        metadata,
        scenarios: includedScenarios
      });
    } catch (error) {
      console.error('Combined PDF generation error:', error);
      alert('Error generating combined PDF. Please try again.');
    } finally {
      setIsGeneratingCombined(false);
    }
  };

  const effectiveSteelGroutBond = getSteelGroutBond(pileInputs);
  const bondMode = pileInputs.steelGroutBondMode || 'manual';

  return (
    <div className="space-y-6" data-testid="technical-report-tab">
      {/* Report Metadata Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Report Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Company Branding Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pb-6 border-b border-border">
            <div className="form-group">
              <Label htmlFor="companyName" className="form-label">Company Name</Label>
              <Input
                id="companyName"
                value={metadata.companyName}
                onChange={(e) => updateMetadata('companyName', e.target.value)}
                placeholder="e.g., Darkocean SPC LLC"
                data-testid="input-company-name"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="logoUrl" className="form-label">Logo URL (Optional)</Label>
              <Input
                id="logoUrl"
                value={metadata.logoUrl}
                onChange={(e) => updateMetadata('logoUrl', e.target.value)}
                placeholder="https://example.com/logo.png"
                data-testid="input-logo-url"
              />
            </div>
          </div>

          {/* Project Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="form-group">
              <Label htmlFor="projectName" className="form-label">Project Name</Label>
              <Input
                id="projectName"
                value={metadata.projectName}
                onChange={(e) => updateMetadata('projectName', e.target.value)}
                placeholder="e.g., WHP-A Platform Foundation"
                data-testid="input-project-name"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="client" className="form-label">Client</Label>
              <Input
                id="client"
                value={metadata.client}
                onChange={(e) => updateMetadata('client', e.target.value)}
                placeholder="e.g., Offshore Energy Ltd."
                data-testid="input-client"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="location" className="form-label">Location</Label>
              <Input
                id="location"
                value={metadata.location}
                onChange={(e) => updateMetadata('location', e.target.value)}
                placeholder="e.g., North Sea, Block 42"
                data-testid="input-location"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="pileId" className="form-label">Pile ID</Label>
              <Input
                id="pileId"
                value={metadata.pileId}
                onChange={(e) => updateMetadata('pileId', e.target.value)}
                placeholder="e.g., P-01"
                data-testid="input-pile-id"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="preparedBy" className="form-label">Prepared By</Label>
              <Input
                id="preparedBy"
                value={metadata.preparedBy}
                onChange={(e) => updateMetadata('preparedBy', e.target.value)}
                placeholder="Engineer name"
                data-testid="input-prepared-by"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="checkedBy" className="form-label">Checked By</Label>
              <Input
                id="checkedBy"
                value={metadata.checkedBy}
                onChange={(e) => updateMetadata('checkedBy', e.target.value)}
                placeholder="Reviewer name"
                data-testid="input-checked-by"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="date" className="form-label">Date</Label>
              <Input
                id="date"
                type="date"
                value={metadata.date}
                onChange={(e) => updateMetadata('date', e.target.value)}
                data-testid="input-date"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="revision" className="form-label">Revision</Label>
              <Input
                id="revision"
                value={metadata.revision}
                onChange={(e) => updateMetadata('revision', e.target.value)}
                placeholder="e.g., 0, A, B"
                data-testid="input-revision"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Narrative & Comments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Report Narrative & Comments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="form-group">
            <Label htmlFor="executiveSummary" className="form-label">
              Executive Summary / Introduction (Optional)
            </Label>
            <Textarea
              id="executiveSummary"
              value={narrative.executiveSummary}
              onChange={(e) => updateNarrative('executiveSummary', e.target.value)}
              placeholder="Provide a brief project context and purpose of this capacity assessment..."
              className="min-h-[100px]"
              data-testid="input-executive-summary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This text will appear in Section 1 (Introduction) of the PDF report.
            </p>
          </div>

          <div className="form-group">
            <Label htmlFor="designAssumptions" className="form-label">
              Design Assumptions & Notes (Optional)
            </Label>
            <Textarea
              id="designAssumptions"
              value={narrative.designAssumptions}
              onChange={(e) => updateNarrative('designAssumptions', e.target.value)}
              placeholder="Document any project-specific assumptions, design criteria, or site conditions..."
              className="min-h-[100px]"
              data-testid="input-design-assumptions"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This text will appear in Section 2 (Design Basis) as additional notes.
            </p>
          </div>

          <div className="form-group">
            <Label htmlFor="recommendations" className="form-label">
              Recommendations (Optional)
            </Label>
            <Textarea
              id="recommendations"
              value={narrative.recommendations}
              onChange={(e) => updateNarrative('recommendations', e.target.value)}
              placeholder="Add project-specific recommendations or next steps..."
              className="min-h-[100px]"
              data-testid="input-recommendations"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This text will be added to Section 6 (Results & Recommendations).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Design Scenarios */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileStack className="h-5 w-5" />
            Design Scenarios
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Save New Scenario */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6 pb-6 border-b border-border">
            <div className="flex-1">
              <Input
                value={newScenarioName}
                onChange={(e) => setNewScenarioName(e.target.value)}
                placeholder={`e.g., "Baseline 24" @ 23.5m" or "Option 1: 30" @ 30m"`}
                data-testid="input-scenario-name"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleSaveScenario}
              disabled={!results}
              data-testid="save-scenario-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Current as Scenario
            </Button>
          </div>

          {/* Scenarios List */}
          {scenarios && scenarios.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Include</TableHead>
                    <TableHead>Scenario Name</TableHead>
                    <TableHead>Pile ID</TableHead>
                    <TableHead className="text-right">Q<sub>u</sub> (MN)</TableHead>
                    <TableHead className="text-right">Q<sub>allow</sub> (MN)</TableHead>
                    <TableHead className="text-right">Min FS</TableHead>
                    <TableHead>Saved</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scenarios.map((scenario) => (
                    <TableRow key={scenario.id} data-testid={`scenario-row-${scenario.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={scenario.includeInReport}
                          onCheckedChange={() => toggleScenarioInclusion(scenario.id)}
                          data-testid={`scenario-checkbox-${scenario.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{scenario.name}</TableCell>
                      <TableCell>{scenario.pileId}</TableCell>
                      <TableCell className="text-right font-mono">
                        {scenario.results?.ultimateCapacity?.toFixed(3) || '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {scenario.results?.allowableCapacity?.toFixed(3) || '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {scenario.fos?.minFS?.toFixed(2) || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(scenario.savedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteScenario(scenario.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-scenario-${scenario.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileStack className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No scenarios saved yet.</p>
              <p className="text-sm mt-1">
                Save the current design as a scenario to compare multiple options.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PDF Appendix Uploads */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileStack className="h-5 w-5" />
            PDF Appendices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload PDF documents to include as appendices in the final report (borehole logs, site investigation data, etc.)
          </p>
          
          {/* Upload buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Borehole Log Upload */}
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="font-medium">Borehole Log</Label>
              <p className="text-xs text-muted-foreground">Upload borehole/soil investigation PDF</p>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleAppendixUpload(e, 'borehole')}
                  className="text-sm"
                  data-testid="borehole-pdf-upload"
                />
              </div>
            </div>
            
            {/* Other PDF Upload */}
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="font-medium">Other Documents</Label>
              <p className="text-xs text-muted-foreground">Upload additional reference PDFs</p>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleAppendixUpload(e, 'other')}
                  className="text-sm"
                  data-testid="other-pdf-upload"
                />
              </div>
            </div>
          </div>
          
          {/* List of uploaded appendices */}
          {appendices.length > 0 && (
            <div className="space-y-2 mt-4">
              <Label className="font-medium">Uploaded Appendices ({appendices.length})</Label>
              <div className="border rounded-lg divide-y">
                {appendices.map((appendix, index) => (
                  <div key={appendix.id} className="p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-[#00284F] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {String.fromCharCode(65 + index + 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Input
                        value={appendix.title}
                        onChange={(e) => updateAppendixTitle(appendix.id, e.target.value)}
                        placeholder="Appendix title"
                        className="h-8 text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {appendix.name} ({(appendix.size / 1024).toFixed(1)} KB)
                        {appendix.type === 'borehole' && <span className="ml-2 text-blue-600">[Borehole Log]</span>}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAppendix(appendix.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Contents Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Report Contents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { num: '1', title: 'Cover Page', desc: 'Project metadata, title, and document control' },
              { num: '2', title: 'Introduction', desc: 'Purpose, methodology (Abbs & Needham model)' },
              { num: '3', title: 'Design Basis', desc: 'Pile geometry, material properties' },
              { num: '4', title: 'Geotechnical Parameters', desc: 'Soil/rock layers with T_RG correlation' },
              { num: '5', title: 'Capacity Calculations', desc: 'Steel, grout-bond, and shaft capacity' },
              { num: '6', title: 'Load-Settlement Behaviour', desc: 'Chart and response narrative' },
              { num: '7', title: 'Results & Recommendations', desc: 'Summary and engineering commentary' },
              { num: 'A', title: 'Appendices', desc: 'FOS checks, sensitivity analysis (if available)' },
            ].map((item) => (
              <div key={item.num} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                <div className={`w-7 h-7 rounded ${item.num === 'A' ? 'bg-[#00284F]/70' : 'bg-[#00284F]'} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                  {item.num}
                </div>
                <div className="min-w-0">
                  <h4 className="font-medium text-sm">{item.title}</h4>
                  <p className="text-xs text-muted-foreground truncate">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Results Status Warning */}
      {!results && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium text-amber-700 dark:text-amber-400">Analysis Required</p>
                <p className="text-sm text-muted-foreground">
                  Please run the analysis first (click "Run Analysis" in the header) before generating the report.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generate Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Single Pile Report */}
        <Card className="bg-[#00284F]/5 dark:bg-[#00284F]/10 border-[#00284F]/20">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="font-semibold text-lg">Single Pile Report</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate a detailed A4 PDF for the current pile
                </p>
              </div>
              <Button
                size="lg"
                onClick={handleGenerateReport}
                disabled={isGenerating || !results}
                className="bg-[#00284F] hover:bg-[#00284F]/90 w-full"
                data-testid="generate-report-btn"
              >
                <Download className="h-5 w-5 mr-2" />
                {isGenerating ? 'Generating...' : 'Generate PDF Report'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Combined Multi-Scenario Report */}
        <Card className="bg-emerald-500/5 border-emerald-500/20">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="font-semibold text-lg">Combined Scenario Report</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate a multi-scenario comparison PDF
                  {scenarios?.filter(s => s.includeInReport).length > 0 && (
                    <span className="ml-1">
                      ({scenarios.filter(s => s.includeInReport).length} scenarios selected)
                    </span>
                  )}
                </p>
              </div>
              <Button
                size="lg"
                variant="outline"
                onClick={handleGenerateCombinedReport}
                disabled={isGeneratingCombined || !scenarios?.some(s => s.includeInReport)}
                className="border-emerald-500 hover:bg-emerald-500/10 w-full"
                data-testid="generate-combined-report-btn"
              >
                <FileStack className="h-5 w-5 mr-2" />
                {isGeneratingCombined ? 'Generating...' : 'Generate Combined Report'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Summary */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Current Design Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Pile Sections:</span>
                <span className="ml-2 font-mono font-medium">{pileInputs.pileSections.length}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Soil Layers:</span>
                <span className="ml-2 font-mono font-medium">{soilLayers.length}</span>
              </div>
              <div>
                <span className="text-muted-foreground">T<sub>sg</sub>:</span>
                <span className="ml-2 font-mono font-medium">
                  {effectiveSteelGroutBond.toFixed(2)} MPa ({bondMode})
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Q<sub>u</sub>:</span>
                <span className="ml-2 font-mono font-medium">{results.ultimateCapacity.toFixed(3)} MN</span>
              </div>
              <div>
                <span className="text-muted-foreground">Sensitivity:</span>
                <span className="ml-2 font-mono font-medium">
                  {sensitivityResults && sensitivityResults.length > 0 
                    ? `${sensitivityResults.length} points` 
                    : 'None'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
